import React, { useState, useEffect, useRef } from 'react';
import { createClient, updateClient, uploadClientLogo } from '../services/clientService';
import io from 'socket.io-client';
import {
    Loader2, Building2, FileText,
    CheckCircle2, Circle, ArrowRight, ShieldCheck,
    UploadCloud, ImageIcon, Trash2,
    MessageCircle, Video, Home, ClipboardList, CheckSquare, PenTool, Users, Server, Cpu, Terminal, CreditCard, Package, ShoppingCart, GraduationCap, Bus, CalendarCheck
} from 'lucide-react';

const MODULE_OPTIONS = [
    { key: 'module_chat', label: 'Chat & Messaging', description: 'Real-time communication', icon: MessageCircle, color: 'text-violet-500' },
    { key: 'module_visio', label: 'Video Meetings', description: 'Integrated video conferencing', icon: Video, color: 'text-blue-500' },
    { key: 'module_custom_home', label: 'Custom Homepage', label_fr: 'Page d\'accueil personnalisable', description: 'Branded landing experience', icon: Home, color: 'text-orange-500' },
    { key: 'module_activites', label: 'Activities Management', description: 'Track tasks and events', icon: ClipboardList, color: 'text-amber-500' },
    { key: 'module_qcm', label: 'Quizzes & Exams', description: 'Assessment tools (QCM)', icon: CheckSquare, color: 'text-emerald-500' },
    { key: 'module_devoir', label: 'Assignments', description: 'Homework & corrections', icon: PenTool, color: 'text-rose-500' },
    { key: 'module_document', label: 'Document Center', description: 'Secure file sharing', icon: FileText, color: 'text-indigo-500' },
    { key: 'module_finance', label: 'Finance & Paiements', description: 'Gestion financière et paiements', icon: CreditCard, color: 'text-green-500' },
    { key: 'module_depots', label: 'Gestion des Dépôts', description: 'Gestion de stock', icon: Package, color: 'text-yellow-500' },
    { key: 'module_produits', label: 'Catalogue Produits', description: 'Gestion des produits', icon: ShoppingCart, color: 'text-cyan-500' },
    { key: 'module_scolarite', label: 'Scolarité', description: 'Bulletins et semestres', icon: GraduationCap, color: 'text-pink-500' },
    { key: 'module_bus', label: 'Transport Scolaire', description: 'Gestion des bus', icon: Bus, color: 'text-sky-500' },
    { key: 'module_disponibilites', label: 'Disponibilités', description: 'Disponibilités des professeurs (désactivé par défaut)', icon: CalendarCheck, color: 'text-teal-500' },
];

const ClientForm = ({ initialData, isEdit, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState(
        initialData || {
            name: '',
            hostName: '',
            Espace: '',
            cin: '',
            client_name: '',
            company_name: '',
            vpsId: '',
            maxUsers: '',
            module_chat: 1,
            module_visio: 1,
            module_custom_home: 1,
            module_activites: 1,
            module_qcm: 1,
            module_devoir: 1,
            module_document: 1,
            module_finance: 1,
            module_depots: 1,
            module_produits: 1,
            module_scolarite: 1,
            module_bus: 1,
            module_disponibilites: 0,
        });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(initialData?.logo || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [isDeploying, setIsDeploying] = useState(false);
    const [deployLogs, setDeployLogs] = useState([]);
    const [deploySuccess, setDeploySuccess] = useState(false);
    const logsEndRef = useRef(null);

    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [deployLogs, isDeploying]);

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
            setLogoPreview(initialData.logo || null);
        }
    }, [initialData]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleModuleToggle = (key) => {
        setFormData(prev => ({ ...prev, [key]: prev[key] === 1 ? 0 : 1 }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!isEdit) {
            if (!formData.cin) {
                setError("Identifiant officiel (CIN) requis pour le déploiement.");
                setLoading(false);
                return;
            }

            setIsDeploying(true);
            setDeployLogs(['🚀 Initialisation du déploiement...']);

            const socketUrl = 'https://admin.xora-learn.ovh';
            const socket = io(socketUrl, { withCredentials: true });

            socket.emit('join:deploy', formData.cin);
            socket.on('deploy:log', (data) => {
                setDeployLogs(prev => [...prev, data.log]);
            });

            try {
                const response = await createClient(formData);
                const clientId = response.client?._id;

                if (logoFile && clientId) {
                    await uploadClientLogo(clientId, logoFile);
                }

                setDeploySuccess(true);
                // We wait for the user to click "Terminer & Fermer" to trigger onSuccess
            } catch (err) {
                setDeployLogs(prev => [...prev, `❌ Erreur critique: ${err.response?.data?.message || err.message}`]);
            } finally {
                setLoading(false);
                setTimeout(() => socket.disconnect(), 2000);
            }
        } else {
            try {
                let clientId = initialData?._id;
                if (clientId) {
                    await updateClient(clientId, formData);
                    if (logoFile) {
                        await uploadClientLogo(clientId, logoFile);
                    }
                }
                if (onSuccess) onSuccess();
            } catch (err) {
                setError(err.response?.data?.message || 'Échec de la mise à jour. Veuillez réessayer.');
            } finally {
                setLoading(false);
            }
        }
    };

    if (isDeploying) {
        return (
            <div className="flex flex-col h-full bg-[#0a0a0a] text-slate-300 font-mono p-6 rounded-xl border border-slate-800 shadow-2xl relative overflow-hidden" style={{ minHeight: '600px' }}>
                <div className="absolute top-0 left-0 w-full h-8 bg-[#1a1a1a] flex items-center px-4 border-b border-slate-800 z-10">
                    <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                        <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
                    </div>
                    <span className="ml-4 text-[10px] text-slate-500 font-bold tracking-widest uppercase flex items-center gap-2">
                        <Terminal size={12} /> Console de Provisioning
                    </span>
                </div>

                <div className="flex-1 overflow-y-auto mt-6 space-y-2 text-[11px] sm:text-xs leading-relaxed custom-scrollbar pb-16">
                    {deployLogs.map((log, index) => (
                        <div key={index} className="flex gap-3 items-start animate-in fade-in slide-in-from-bottom-1">
                            <span className="text-slate-600 shrink-0 select-none">[{new Date().toLocaleTimeString()}]</span>
                            <span className={`${log.includes('❌') || log.toLowerCase().includes('error') || log.includes('Erreur') || log.includes('ERR!') || log.toLowerCase().includes('warn') ? 'text-red-400 font-bold' :
                                log.includes('✅') || log.includes('✨') ? 'text-emerald-400 font-bold' :
                                    log.includes('🛠️') || log.includes('🔍') || log.includes('🚀') ? 'text-blue-400 font-bold' :
                                        'text-slate-300'
                                } break-all whitespace-pre-wrap`}>
                                {log}
                            </span>
                        </div>
                    ))}
                    <div ref={logsEndRef} />
                </div>

                <div className="absolute bottom-0 left-0 w-full p-4 bg-[#0a0a0a]/90 backdrop-blur-sm border-t border-slate-800 flex justify-end gap-3 shrink-0 z-10">
                    {(!loading) && (
                        <button
                            type="button"
                            onClick={() => {
                                setIsDeploying(false);
                                if (deploySuccess && onSuccess) onSuccess();
                                else if (!deploySuccess && onCancel) onCancel();
                            }}
                            className={`px-6 py-2.5 text-xs font-black uppercase tracking-widest transition-colors rounded ${deploySuccess
                                ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(5,150,105,0.3)]'
                                : 'bg-slate-800 hover:bg-slate-700 text-white'
                                }`}
                        >
                            {deploySuccess ? 'Terminer & Fermer' : 'Fermer la console'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            {/* General Information Section */}
            <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                    <Building2 size={12} />
                    Détails du Tenant
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    {/* Organization Name */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Nom de l'Organisation <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none rounded-md"
                            placeholder="Ex: acme_corp"
                        />
                    </div>

                    {/* Client Name */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Le nom du client <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="client_name"
                            value={formData.client_name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none rounded-md"
                            placeholder="Ex: Le nom du client complet"
                        />
                    </div>

                    {/* Subdomain */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Sous-domaine Tenant <span className="text-red-500">*</span></label>
                        <div className="flex">
                            <input
                                type="text"
                                name="hostName"
                                value={formData.hostName}
                                onChange={handleChange}
                                required
                                pattern="^[a-zA-Z0-9_]+$"
                                className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none font-mono rounded-l-md"
                                placeholder="acme"
                            />
                            <div className="px-4 py-3 bg-slate-50 border border-l-0 border-slate-200 text-slate-400 font-mono text-sm flex items-center shrink-0 rounded-r-md">
                                .xora-learn.ovh
                            </div>
                        </div>
                    </div>

                    {/* Registration ID */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Identifiant Officiel (CIN/MF) <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="cin"
                            value={formData.cin}
                            onChange={handleChange}
                            required
                            pattern="^[0-9]+$"
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none uppercase rounded-md"
                            placeholder="Numéro officiel"
                        />
                    </div>

                    {/* Organization Type */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Type de Structure <span className="text-red-500">*</span></label>
                        <select
                            name="Espace"
                            value={formData.Espace}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none text-slate-700 rounded-md"
                        >
                            <option value="" disabled>Sélectionner...</option>
                            <option value="Centre d'étude">Centre d'étude</option>
                            <option value="Centre de formation">Centre de formation</option>
                            <option value="Individuel">Indépendant/Individuel</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Infrastructure Section */}
            <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                    <Server size={12} />
                    Configuration Infrastructure
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <Cpu size={12} className="text-slate-400" />
                            Identifiant du VPS
                        </label>
                        <input
                            type="text"
                            name="vpsId"
                            value={formData.vpsId}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none rounded-md font-mono"
                            placeholder="Ex: vps-ovh-23094"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <Users size={12} className="text-slate-400" />
                            Limite Ressources Max (Utilisateurs)
                        </label>
                        <input
                            type="number"
                            name="maxUsers"
                            value={formData.maxUsers}
                            onChange={handleChange}
                            min="0"
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none rounded-md"
                            placeholder="Ex: 500"
                        />
                    </div>
                </div>
            </div>

            {/* Branding Section */}
            <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                    <ImageIcon size={12} />
                    Branding & Identité Visuelle
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    {/* Company Display Name */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Titre Affiché (Dénomination)</label>
                        <input
                            type="text"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white border border-slate-200 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all outline-none rounded-md"
                            placeholder="Ex: Acme Corporation"
                        />
                    </div>

                    {/* Logo Upload */}
                    <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Logo Signature</label>
                        <div className="flex items-center gap-4">
                            {logoPreview ? (
                                <div className="relative group/logo">
                                    <div className="w-16 h-16 border border-slate-200 bg-white p-1 overflow-hidden rounded-md">
                                        <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-contain" />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removeLogo}
                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-md flex items-center justify-center shadow-lg opacity-0 group-hover/logo:opacity-100 transition-opacity"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center w-16 h-16 border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-800 cursor-pointer transition-all rounded-md">
                                    <UploadCloud size={16} className="text-slate-400" />
                                    <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                                </label>
                            )}
                            <div className="flex-1">
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">MAX 5MB. FORMAT PNG/SVG RECOMMANDÉ.</p>
                                {!logoPreview && (
                                    <button
                                        type="button"
                                        onClick={() => document.querySelector('input[type="file"]').click()}
                                        className="mt-1 text-[10px] font-bold text-slate-900 hover:text-blue-600 uppercase tracking-widest"
                                    >
                                        Parcourir
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Module Configuration Section */}
            <div>
                <div className="flex items-center justify-between pb-2 border-b border-slate-200 mb-4">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                        <ShieldCheck size={12} />
                        Modules Applicatifs (Feature Toggles)
                    </h3>
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        {Object.values(formData).filter(v => v === 1).length} Activés
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200 p-px rounded-xl overflow-hidden">
                    {MODULE_OPTIONS.map(({ key, label, description, icon: Icon, color }) => {
                        const isActive = formData[key] === 1;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => handleModuleToggle(key)}
                                className={`group relative flex items-start gap-3 p-4 text-left transition-all duration-200 ${isActive
                                    ? 'bg-white'
                                    : 'bg-slate-50 opacity-60 hover:opacity-100 hover:bg-white'
                                    }`}
                            >
                                <div className={`flex items-center justify-center w-8 h-8 transition-transform duration-300 ${isActive ? 'scale-110' : 'grayscale opacity-50'}`}>
                                    <Icon size={24} className={isActive ? color : 'text-slate-400'} strokeWidth={1.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className={`text-xs font-bold uppercase tracking-widest truncate ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                                        {label}
                                    </h5>
                                    <p className="text-[10px] font-bold tracking-wider text-slate-400 truncate uppercase mt-0.5">
                                        {description}
                                    </p>
                                </div>
                                <div className="flex items-center justify-center pt-1">
                                    {isActive ? (
                                        <CheckCircle2 size={16} className="text-emerald-500" />
                                    ) : (
                                        <Circle size={16} className="text-slate-300" />
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-50/50 border border-red-100 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                    <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">!</span>
                    </div>
                    <p className="text-sm text-red-600 font-medium leading-relaxed">{error}</p>
                </div>
            )}

            {/* Form Actions */}
            <div className="pt-6 mt-8 border-t border-slate-200 flex items-center justify-between">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex-1 pr-4 hidden sm:block">
                    {isEdit ? 'Les modifications prennent effet immédiatement.' : 'Le provisioning de l\'instance est instantané.'}
                </p>
                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                    <button
                        type="button"
                        onClick={() => { if (onCancel) onCancel(); else if (onSuccess) onSuccess(false); }}
                        className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors rounded-md hover:bg-slate-100"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-8 py-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 hover:bg-emerald-700 transition-colors rounded-md"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                {isEdit ? 'Mise à jour...' : 'Orchestration...'}
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                {isEdit ? 'Enregistrer' : 'Déployer Instance'}
                                <ArrowRight size={14} />
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default ClientForm;

